import { useState, useCallback, useEffect, useMemo } from 'react'
import useFavurTranslation from 'hooks/useFavurTranslation'
import { useJamesApolloQuery } from 'hooks/useJamesApolloQuery'
import { getNativeVersion, isNative } from 'utils/platform'
import { SHOW_ONLY_ONCE_KEY } from './constants'
import { nativeVersionRequirementQuery } from './queries'
import type {
  NativeVersionObjectT,
  NativeVersionRequirementQueryReturnT,
  CapacitorPlatformT,
  TUseOldNativeVersionDialog,
  UseOldNativeVersionDialogProps,
} from './types'
import { capacitorPlatformTypes } from './types'

export const useOldNativeVersionDialog = ({
  showOnlyOnce,
}: UseOldNativeVersionDialogProps): TUseOldNativeVersionDialog => {
  const { t } = useFavurTranslation()
  const [showDialog, setShowDialog] = useState(false)
  const [nativeVersion, setNativeVersion] = useState<NativeVersionObjectT>()

  useJamesApolloQuery<NativeVersionRequirementQueryReturnT>(nativeVersionRequirementQuery, {
    skip: !nativeVersion,
    variables: {
      platform: nativeVersion?.platform,
      nativeVersionNumber: nativeVersion?.version,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      setShowDialog(!data.nativeVersionRequirement.requirementMet)
    },
  })

  const getPlatformText = useCallback(
    (platform: CapacitorPlatformT) => {
      switch (platform) {
        case capacitorPlatformTypes.android:
          return t('common.platform.android')
        case capacitorPlatformTypes.ios:
          return t('common.platform.ios')
        default:
          return ''
      }
    },
    [t],
  )
  const platformText = useMemo(
    () => (nativeVersion ? getPlatformText(nativeVersion.platform) : ''),
    [getPlatformText, nativeVersion],
  )

  useEffect(() => {
    const alreadyShown = localStorage.getItem(SHOW_ONLY_ONCE_KEY) === 'true' && showOnlyOnce

    if (alreadyShown || !isNative()) return

    const doGetNativeVersion = async () => setNativeVersion(await getNativeVersion())

    doGetNativeVersion()
  }, [showOnlyOnce])

  const onClose = useCallback(() => {
    setShowDialog(false)
    if (showOnlyOnce) {
      localStorage.setItem(SHOW_ONLY_ONCE_KEY, 'true')
    }
  }, [showOnlyOnce])

  return {
    getPlatformText,
    showDialog,
    setShowDialog,
    nativeVersion,
    platformText,
    onClose,
  }
}
